import { getOverviewData, getAnalyticsData, getRoomById, clearPerformanceCache, updateCurrentFilterState } from '../services/performanceService'
import performanceService from '../services/performanceService'
import { normalizeAnalyticsFilter } from '../../common/services/filterService'

/**
 * A refresh that started for an older company, site, or date range must not
 * write over data after the user has already moved on. Custom dates update the
 * store before the previous preset request returns.
 * @param {import('vuex').ActionContext} context
 * @param {Record<string, any>} filter
 */
function isRequestForCurrentFilter(context, filter) {
    const currentFilter = context.rootState.filter.filterSelected;
    if (!currentFilter || !filter) return true;
    if (currentFilter.company !== filter.company) return false;
    if (currentFilter.site !== filter.site) return false;
    const currentTime = currentFilter.time || {};
    const requestedTime = filter.time || {};
    if (currentTime.timeFrom !== requestedTime.timeFrom) return false;
    if (currentTime.timeTo !== requestedTime.timeTo) return false;
    return true;
}

const OVERVIEW_COMPLAINT_DEVICES_STRIP_STORAGE_KEY = 'buzzztv_overview_show_complaint_devices_installed'

function readStoredComplaintDevicesStripPref() {
    try {
        const v = localStorage.getItem(OVERVIEW_COMPLAINT_DEVICES_STRIP_STORAGE_KEY)
        if (v === '0' || v === 'false') {
            return false
        }
        if (v === '1' || v === 'true') {
            return true
        }
    } catch (e) {
        /* ignore */
    }
    return false
}

export default {
    state: {
        overviewData: null,
        analyticsData: null,
        /**@type {Object<string, { value: number; trendInfo: string; }>} */
        barItemsData: null,
        chartListData: null,
        room: {},
        /**@type {Object<string, { text: String; upDown: 'up' | 'down' }> | null} */
        trendInfo: null,
        /** Filter key used when analyticsData was last loaded successfully */
        loadedAnalyticsFilterKey: null,
        /** Overview: show cumulative devices-installed strip under complaints (chevron toggle; persisted in localStorage). */
        overviewShowComplaintDevicesInstalled: readStoredComplaintDevicesStripPref()
    },
    getters: {
        overviewData(state) {
            return state.overviewData
        },
        analyticsData(state) {
            return state.analyticsData
        },
        room(state) {
            return state.room
        },
        chartListData(state) {
            return state.chartListData
        },
        /**@returns {Object<string, { value: number | string; trendInfo: string; }>} */
        barItemsData(state) {
            return state.barItemsData
        },
        /**@returns {Object<string, { text: String; upDown: 'up' | 'down' }> | null} */
        trendInfo(state) {
            return state.trendInfo
        },
        overviewShowComplaintDevicesInstalled(state) {
            return state.overviewShowComplaintDevicesInstalled
        },
        loadedAnalyticsFilterKey(state) {
            return state.loadedAnalyticsFilterKey
        }
    },
    mutations: {
        setOverviewData(state, overviewData) {
            state.overviewData = overviewData
        },
        setAnalyticsData(state, analyticsData) {
            state.analyticsData = analyticsData
        },
        setRoom(state, room) {
            state.room = room
        },
        setBarItemsData(state, barItemsData) {
            state.barItemsData = barItemsData
        },
        setChartListData(state, chartListData) {
            state.chartListData = chartListData
        },
        setTrendInfo(state, trendInfo) {
            state.trendInfo = trendInfo
        },
        setOverviewShowComplaintDevicesInstalled(state, value) {
            state.overviewShowComplaintDevicesInstalled = !!value
            try {
                localStorage.setItem(
                    OVERVIEW_COMPLAINT_DEVICES_STRIP_STORAGE_KEY,
                    state.overviewShowComplaintDevicesInstalled ? '1' : '0'
                )
            } catch (e) {
                /* ignore */
            }
        },
        setLoadedAnalyticsFilterKey(state, filterKey) {
            state.loadedAnalyticsFilterKey = filterKey
        },
        clearPerformanceData(state) {
            state.overviewData = null
            state.analyticsData = null
            state.barItemsData = null
            state.chartListData = null
            state.room = {}
            state.trendInfo = null
            state.loadedAnalyticsFilterKey = null
        }
    },
    actions: {
        async getOverviewData(context, payload) {
            const { filter, forceRefresh = false } = payload;
            
            // Update current filter state in service
            updateCurrentFilterState(filter);

            if (!isRequestForCurrentFilter(context, filter)) {
                return null;
            }
            
            // Clear cache if force refresh is requested
            if (forceRefresh) {
                clearPerformanceCache(filter);
            }
            const overviewData = await getOverviewData(filter)
            if (!isRequestForCurrentFilter(context, filter)) {
                return null;
            }
            context.commit('setOverviewData', overviewData)
            // Compute and cache trendInfo when overviewData is updated
            await context.dispatch('computeTrendInfo', { filter })
            return overviewData
        },
        async getAnalyticsData(context, payload) {
            const { filter } = payload;
            
            // Update current filter state in service
            updateCurrentFilterState(filter);

            if (!isRequestForCurrentFilter(context, filter)) {
                return null;
            }
            
            // Analytics data is not cached - always fetch fresh
            const analyticsData = await getAnalyticsData(filter)
            if (!isRequestForCurrentFilter(context, filter)) {
                return null;
            }
            context.commit('setAnalyticsData', analyticsData)
            if (analyticsData) {
                context.commit('setLoadedAnalyticsFilterKey', normalizeAnalyticsFilter(filter))
            } else {
                context.commit('setLoadedAnalyticsFilterKey', null)
            }
            return analyticsData
        },
        async getRoomById(context, { id }) {
            const room = await getRoomById(id)
            context.commit('setRoom', room)
            return room
        },
        async computeTrendInfo(context, { filter }) {
            const { state, rootState } = context
            if (!state.overviewData) {
                context.commit('setTrendInfo', null)
                return null
            }
            // Always use the current filterSelected, not a potentially stale filter
            // This prevents trendInfoForBarItems from making API calls with old company/site values
            const currentFilter = rootState.filter.filterSelected;
            const filterToUse = currentFilter || filter;
            const requestedTime = (filterToUse && filterToUse.time) || {};
            const requestedFrom = requestedTime.timeFrom;
            const requestedTo = requestedTime.timeTo;
            const requestedCompany = filterToUse && filterToUse.company;
            const requestedSite = filterToUse && filterToUse.site;
            
            try {
                const trendInfo = await performanceService.trendInfoForBarItems(state.overviewData, filterToUse)
                // Only update if the range that produced overviewData is still selected.
                const latestFilter = rootState.filter.filterSelected;
                const latestTime = (latestFilter && latestFilter.time) || {};
                if (
                    latestFilter &&
                    latestFilter.company === requestedCompany &&
                    latestFilter.site === requestedSite &&
                    latestTime.timeFrom === requestedFrom &&
                    latestTime.timeTo === requestedTo
                ) {
                    context.commit('setTrendInfo', trendInfo)
                    return trendInfo
                }
                // Filter changed during computation, return null
                return null
            } catch (error) {
                // On error, set to empty object instead of null to prevent property access errors
                context.commit('setTrendInfo', {})
                return {}
            }
        }
    }
}